import {
  createFormFieldAdapter,
  type FieldEventHandler,
  type FieldRoot,
  type FormFieldAdapter,
  type NamedField,
  type SerializedFieldValue
} from "../../phase2/form-field-adapter";

export {
  createFormFieldAdapter,
  type FieldEventHandler,
  type FieldRoot,
  type FormFieldAdapter,
  type NamedField,
  type SerializedFieldValue
};

export interface InstalledFormFieldAdapter {
  adapter: FormFieldAdapter;
  nativeElements: HTMLFormControlsCollection;
  proxyElements: HTMLFormControlsCollection;
}

const collectionProperties = new Set([
  "constructor",
  "entries",
  "forEach",
  "item",
  "keys",
  "length",
  "namedItem",
  "values"
]);

function isNumericProperty(property: string): boolean {
  return /^(0|[1-9]\d*)$/.test(property);
}

function isCollectionProperty(property: string): boolean {
  return collectionProperties.has(property) || isNumericProperty(property);
}

/**
 * Keeps the legacy form.elements[name] contract while routing named field
 * access through the MDS-aware adapter. Native collection methods remain
 * bound to the original HTMLFormControlsCollection.
 */
export function installFormFieldAdapter(form: HTMLFormElement): InstalledFormFieldAdapter {
  const nativeElements = form.elements;
  const adapter = createFormFieldAdapter(form);
  const proxyElements = new Proxy(nativeElements, {
    get(target, property, receiver) {
      if (typeof property === "string" && !isCollectionProperty(property)) {
        const field = adapter.getField(property);
        if (field) {
          return field;
        }
      }

      const value = Reflect.get(target, property, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
    set(target, property, value, receiver) {
      if (typeof property === "string" && !isCollectionProperty(property)) {
        const field = adapter.getField(property);
        if (field) {
          adapter.writeFieldValue(property, value as string | number | boolean | null | undefined);
          return true;
        }
      }

      return Reflect.set(target, property, value, receiver);
    }
  });

  Object.defineProperty(form, "elements", {
    configurable: true,
    enumerable: false,
    value: proxyElements,
    writable: false
  });

  return { adapter, nativeElements, proxyElements };
}
